import {
  LOCATION_OPTION_TYPES,
  type LocationOptionDto,
} from "services/suggestion/location-options.service";

export type LocationId = string | number;

export type LocationRecord = Record<string, unknown>;

export type LocationOption = {
  value: string;
  label: string;
  locationId?: LocationId;
  locationType?: string;
  type?: number;
  referenceId?: LocationId;
  code?: string | null;
  description?: string | null;
  customerCode?: string | null;
  raw?: unknown;
  isLegacy?: boolean;
};

export type LocationReadDto = {
  type?: number | null;
  referenceId?: LocationId | null;
  code?: string | null;
  description?: string | null;
  customerCode?: string | null;
};

export const toLocationReadDto = (
  location: LocationOption | null
): LocationReadDto | null =>
  location && {
    type: location.type ?? null,
    referenceId: location.referenceId ?? location.locationId ?? null,
    code: location.code ?? null,
    description: location.description ?? null,
    ...(location.customerCode !== undefined
      ? { customerCode: location.customerCode }
      : {}),
  };

const PICK_UP_LOCATION_KEYS = [
  "PickUpLocation",
  "PickupLocation",
  "PickUpLocationId",
  "PickupLocationId",
];

const DELIVERY_LOCATION_KEYS = ["DeliveryLocation", "DeliveryLocationId"];

const isRecord = (value: unknown): value is LocationRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getRecords = (value: unknown): LocationRecord[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const getFirstValue = (object: LocationRecord | null | undefined, keys: string[]): unknown => {
  if (!object) return undefined;

  for (const key of keys) {
    const value = object[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return undefined;
};

const getFirstText = (object: LocationRecord | null | undefined, keys: string[]): string => {
  const value = getFirstValue(object, keys);
  return value === undefined || value === null ? "" : String(value);
};

const asLocationId = (value: unknown): LocationId | undefined =>
  typeof value === "string" || typeof value === "number" ? value : undefined;

const getLocationTypeName = (type: number): string => {
  switch (type) {
    case LOCATION_OPTION_TYPES.SUPPLIER:
      return "Supplier";
    case LOCATION_OPTION_TYPES.SUPPLIER_ORIGIN:
      return "SupplierOrigin";
    case LOCATION_OPTION_TYPES.PLANT:
      return "Plant";
    case LOCATION_OPTION_TYPES.CONSIGNEE_STOCK_SUPPLIER:
      return "ConsigneeStockSupplier";
    default:
      return "Location";
  }
};

export const normalizeLocationOption = (option: LocationOptionDto): LocationOption => ({
  value: `${option.type}:${String(option.referenceId)}`,
  label: option.description ?? option.code ?? String(option.referenceId),
  locationId: option.referenceId,
  locationType: getLocationTypeName(option.type),
  type: option.type,
  referenceId: option.referenceId,
  code: option.code,
  description: option.description,
  customerCode: option.customerCode,
  raw: option,
});

export const normalizeLocationOptions = (options: LocationOptionDto[] = []): LocationOption[] =>
  uniqueByValue(
    options.filter((option) => option && option.isSelectable !== false).map(normalizeLocationOption)
  );

const getRecordId = (record: LocationRecord | null | undefined): LocationId | undefined =>
  asLocationId(record?.Id ?? record?.id);

export const filterPickUpOptionsBySupplier = (
  options: LocationOptionDto[] | undefined,
  supplier: LocationRecord | null | undefined
): LocationOptionDto[] => {
  const supplierId = getRecordId(supplier);

  if (supplierId === undefined) return [];

  return (options ?? []).filter((option) => {
    if (option.type === LOCATION_OPTION_TYPES.SUPPLIER) {
      return String(option.referenceId) === String(supplierId);
    }

    // The API response is already scoped to the selected supplier.
    if (option.type === LOCATION_OPTION_TYPES.SUPPLIER_ORIGIN) return true;

    return false;
  });
};

export const isSupplierOriginOption = (option: LocationOption | LocationOptionDto): boolean =>
  option.type === LOCATION_OPTION_TYPES.SUPPLIER_ORIGIN;

const isEligible = (option: LocationRecord): boolean => {
  const flags = [
    option.IsActive,
    option.isActive,
    option.Active,
    option.active,
    option.IsEligible,
    option.isEligible,
    option.Eligible,
    option.eligible,
    option.IsEligibleForAllocation,
    option.IsAllocationEligible,
    option.EligibleInAllocation,
    option.IsEligibleInChess,
    option.IsXadrezEligible,
  ];

  return flags.every((flag) => flag !== false);
};

const isAssociatedWithDestination = (
  option: LocationRecord,
  destination: LocationRecord | null | undefined
): boolean => {
  if (!destination || !option) return true;

  const destinationId = asLocationId(destination.Id ?? destination.id);
  const destinationCode = destination.Code ?? destination.code;
  const optionDestinationId = asLocationId(
    option.BusinessUnitId ??
      option.IdUnidadeNegocio ??
      option.PlantId ??
      option.DestinationBusinessUnitId
  );
  const optionDestinationCode = option.Centro ?? option.Plant ?? option.PlantCode;

  if (optionDestinationId !== undefined && destinationId !== undefined) {
    return String(optionDestinationId) === String(destinationId);
  }

  if (optionDestinationCode && destinationCode) {
    return String(optionDestinationCode) === String(destinationCode);
  }

  return true;
};

const uniqueByValue = (options: Array<LocationOption | null | false>): LocationOption[] => {
  const seen = new Set<string>();

  return options.filter((option): option is LocationOption => {
    if (!option || option.value === undefined || option.value === null) return false;
    if (seen.has(String(option.value))) return false;
    seen.add(String(option.value));
    return true;
  });
};

const normalizeOrigin = (origin: LocationRecord): LocationOption => {
  const id = asLocationId(origin.id ?? origin.Id);

  return {
    value: `supplier-origin:${String(id)}`,
    label: getFirstText(origin, ["name", "Name", "Description", "Descricao"]),
    locationId: id,
    locationType: "SupplierOrigin",
    type: LOCATION_OPTION_TYPES.SUPPLIER_ORIGIN,
    referenceId: id,
    code: getFirstText(origin, ["code", "Code", "CodigoFornecedor", "CodigoFornecedorMdg"]) || null,
    description: getFirstText(origin, ["description", "Description", "Descricao", "name", "Name"]) || null,
    raw: origin,
  };
};

const normalizeSupplier = (supplier: LocationRecord): LocationOption => {
  const id = asLocationId(supplier.Id ?? supplier.id);

  return {
    value: `supplier:${String(id)}`,
    label: getFirstText(supplier, [
      "SupplierCode",
      "SupplierCodeMdg",
      "CodigoFornecedor",
      "CodigoFornecedorMdg",
      "Descricao",
    ]),
    locationId: id,
    locationType: "Supplier",
    type: LOCATION_OPTION_TYPES.SUPPLIER,
    referenceId: id,
    code: getFirstText(supplier, ["code", "Code", "SupplierCode", "CodigoFornecedor"]) || null,
    description: getFirstText(supplier, ["description", "Description", "Descricao"]) || null,
    raw: supplier,
  };
};

const normalizeConsigneeStock = (supplier: LocationRecord): LocationOption => {
  const id = asLocationId(
    getFirstValue(supplier, [
      "Id",
      "id",
      "SupplierId",
      "CustomerId",
      "CustomerSap",
      "SupplierCodeMdg",
    ])
  );

  return {
    value: `consignee-stock:${String(id)}`,
    label: getFirstText(supplier, [
      "Descricao",
      "Description",
      "Nome",
      "Name",
      "CustomerDisplayName",
      "CustomerSap",
      "SupplierCodeMdg",
    ]),
    locationId: id,
    locationType: "ConsigneeStock",
    type: LOCATION_OPTION_TYPES.CONSIGNEE_STOCK_SUPPLIER,
    referenceId: id,
    code: getFirstText(supplier, ["code", "Code", "SupplierCodeMdg", "CustomerSap"]) || null,
    description: getFirstText(supplier, ["description", "Description", "Descricao", "Name"]) || null,
    customerCode: getFirstText(supplier, ["customerCode", "CustomerCode", "CustomerSap"]) || null,
    raw: supplier,
  };
};

export const getLocationValue = (item: LocationRecord | null | undefined, type: string): unknown =>
  (() => {
    const value = getFirstValue(
      item,
      type === "PickUpLocation" ? PICK_UP_LOCATION_KEYS : DELIVERY_LOCATION_KEYS
    );

    // The API can return an empty location object instead of null.
    // It must not be converted to String(value), which produces "[object Object]".
    if (
      isRecord(value) &&
      ["Type", "type", "ReferenceId", "referenceId", "Code", "code", "Description", "description", "CustomerCode", "customerCode"]
        .every((key) => value[key] === undefined || value[key] === null || value[key] === "")
    ) {
      return undefined;
    }

    return value;
  })();

export const getLocationValueOrNull = (
  item: LocationRecord | null | undefined,
  type: string
): unknown => {
  const value = getLocationValue(item, type);

  return value === undefined || value === null || value === "" || value === 0 ? null : value;
};

export const getLocationDescription = (
  item: LocationRecord | null | undefined,
  type: string
): string =>
  getFirstText(
    item,
    type === "PickUpLocation"
      ? [
          "PickUpLocationDescription",
          "PickupLocationDescription",
          "PickUpLocationName",
          "PickupLocationName",
        ]
      : ["DeliveryLocationDescription", "DeliveryLocationName"]
  );

export const getSupplierOrigins = (
  item: LocationRecord | null | undefined,
  supplier: LocationRecord | null | undefined
): LocationRecord[] => {
  const materials = getRecords(item?.Materiais);
  const material = materials.find(
    (materialItem) => String(materialItem.Id) === String(item?.IdMaterial)
  );
  const supplierFromMaterial = getRecords(material?.Fornecedores).find(
    (supplierItem) => String(supplierItem.Id) === String(item?.IdFornecedor)
  );

  const supplierOrigins = getRecords(supplier?.SupplierOrigins);
  if (supplierOrigins.length > 0) return supplierOrigins;

  const legacySupplierOrigins = getRecords(supplier?.supplierOrigins);
  if (legacySupplierOrigins.length > 0) return legacySupplierOrigins;

  const materialSupplierOrigins = getRecords(supplierFromMaterial?.SupplierOrigins);
  if (materialSupplierOrigins.length > 0) return materialSupplierOrigins;

  return getRecords(supplierFromMaterial?.supplierOrigins);
};

export const getConsigneeStockCandidates = (
  item: LocationRecord | null | undefined,
  deliveryLocations: LocationRecord[] = []
): LocationRecord[] => {
  const materials = getRecords(item?.Materiais);
  const material = materials.find(
    (materialItem) => String(materialItem.Id) === String(item?.IdMaterial)
  );
  const materialSuppliers = getRecords(material?.Fornecedores);
  const itemCandidates = [
    ...getRecords(item?.DeliveryLocations),
    ...getRecords(item?.ConsigneeStocks),
    ...getRecords(item?.ConsigneeStockCandidates),
  ];
  const materialCandidates = materialSuppliers.filter(
    (supplier) =>
      supplier.Origem === 4 ||
      supplier.SupplierTypeValue === 4 ||
      supplier.SupplyTypeValue === 4 ||
      supplier.Type === "ConsigneeStock" ||
      supplier.SupplierType === "ConsigneeStock"
  );

  return [...deliveryLocations, ...itemCandidates, ...materialCandidates];
};

export const buildPickUpOptions = (
  supplier: LocationRecord | null | undefined,
  origins: LocationRecord[] = []
): LocationOption[] =>
  uniqueByValue([
    supplier ? normalizeSupplier(supplier) : null,
    ...origins
      .filter((origin) => origin && isEligible(origin) && (origin.id ?? origin.Id) !== undefined)
      .map(normalizeOrigin),
  ]);

export const buildDeliveryOptions = (
  destination: LocationRecord | null | undefined,
  candidates: LocationRecord[] = []
): LocationOption[] => {
  const destinationId = asLocationId(destination?.Id ?? destination?.id ?? destination?.Code);
  const plantOption =
    destinationId !== undefined || destination?.Description
      ? [
          {
            value: `plant:${String(destinationId)}`,
            label: String(destination?.Description ?? destination?.Code ?? destinationId),
            locationId: destinationId,
            locationType: "Plant",
            type: LOCATION_OPTION_TYPES.PLANT,
            referenceId: destinationId,
            code: (destination?.Code ?? destination?.code ?? null) as string | null,
            description: (destination?.Description ?? destination?.description ?? null) as string | null,
            raw: destination,
          },
        ]
      : [];

  const options = candidates
    .filter((candidate) => isEligible(candidate))
    .filter((candidate) => isAssociatedWithDestination(candidate, destination))
    .map(normalizeConsigneeStock);

  const validOptions = options.filter(
    (option) =>
      option.locationId !== undefined && option.locationId !== null && option.locationId !== ""
  );

  return uniqueByValue([...plantOption, ...validOptions]);
};

const getPersistedObjectOptionValue = (
  objectValue: unknown,
  objectType: unknown
): unknown => {
  if (typeof objectValue === "string" && objectValue.includes(":")) return objectValue;
  if (objectType !== undefined && objectValue !== undefined) {
    return `${String(objectType)}:${String(objectValue)}`;
  }

  return objectValue;
};

const getPersistedObjectLocationType = (
  value: LocationRecord,
  objectType: unknown
): string | undefined => {
  if (typeof value.locationType === "string") return value.locationType;
  if (typeof objectType === "number") return getLocationTypeName(objectType);
  return undefined;
};

const isMatchingObjectOption = (
  option: LocationOption,
  objectOptionValue: unknown,
  objectValue: unknown
): boolean => {
  if (option.value === objectOptionValue) return true;
  if (option.value === objectValue) return true;
  return String(option.locationId) === String(objectValue);
};

const buildLegacyObjectOption = (
  value: LocationRecord,
  objectValue: unknown,
  objectType: unknown,
  objectOptionValue: unknown
): LocationOption => {
  const referenceId = asLocationId(
    getFirstValue(value, ["referenceId", "ReferenceId", "Id", "id"])
  );

  return {
    value: String(objectOptionValue ?? objectValue ?? value),
    label:
      getFirstText(value, ["label", "Description", "Descricao", "Name", "name"]) ||
      String(value),
    locationId: referenceId,
    locationType: getPersistedObjectLocationType(value, objectType),
    type: typeof objectType === "number" ? objectType : undefined,
    referenceId,
    code: typeof value.code === "string" ? value.code : undefined,
    description: typeof value.description === "string" ? value.description : undefined,
    customerCode: typeof value.customerCode === "string" ? value.customerCode : undefined,
    raw: value,
    isLegacy: true,
  };
};

const findOptionByPersistedObject = (
  value: LocationRecord,
  options: LocationOption[]
): LocationOption | null => {
  const objectValue = getFirstValue(value, [
    "value",
    "referenceId",
    "ReferenceId",
    "Id",
    "id",
    "Code",
    "code",
  ]);
  const objectType = getFirstValue(value, ["type", "Type"]);
  const objectOptionValue = getPersistedObjectOptionValue(objectValue, objectType);
  const match = options.find((option) =>
    isMatchingObjectOption(option, objectOptionValue, objectValue)
  );

  return (
    match ?? buildLegacyObjectOption(value, objectValue, objectType, objectOptionValue)
  );
};

const findOptionByPersistedScalar = (
  value: unknown,
  options: LocationOption[]
): LocationOption | null =>
  options.find(
    (option) =>
      option.value === value ||
      String(option.locationId) === String(value) ||
      String(option.label) === String(value)
  ) ?? null;

const findOptionByPersistedValue = (
  value: unknown,
  options: LocationOption[]
): LocationOption | null => {
  if (value === undefined || value === null || value === "") return null;
  if (isRecord(value)) return findOptionByPersistedObject(value, options);
  return findOptionByPersistedScalar(value, options);
};

export const getSelectedLocation = (
  item: LocationRecord | null | undefined,
  type: string,
  options: LocationOption[]
): LocationOption | null => {
  const value = getLocationValue(item, type);
  const selectedOption = findOptionByPersistedValue(value, options);

  if (value === 0) return null;
  if (selectedOption) return selectedOption;

  if (value === undefined || value === null || value === "") return null;

  return {
    value: String(value),
    label: getLocationDescription(item, type) || String(value),
    locationId: asLocationId(value),
    locationType: type,
    raw: value,
    isLegacy: true,
  };
};

export const hasLegacyLocation = (
  item: LocationRecord | null | undefined,
  type: string,
  options: LocationOption[]
): boolean => {
  const value = getLocationValue(item, type);
  if (value === 0) return false;
  if (value === undefined || value === null || value === "") return false;

  const selected = findOptionByPersistedValue(value, options);
  return !selected || selected.isLegacy === true;
};

export const mapConsigneeStockCustomers = (customers: LocationRecord[] = []): LocationRecord[] =>
  customers
    .map((customer) => ({
      ...customer,
      Id: customer.SupplierId ?? customer.CustomerId ?? customer.Id ?? customer.CustomerSap,
      Descricao: customer.Name ?? customer.CustomerDisplayName ?? customer.CustomerSap,
      CodigoFornecedor: customer.SupplierCodeMdg ?? customer.CustomerSap,
      Origem: 4,
    }))
    .filter((customer) => customer.Id !== undefined && customer.Id !== null && customer.Id !== "");
